import dayjs from 'dayjs'

import { TaskProgress } from '../../types/taskProgress'
import { TaskTripDetailsLocation, TripStep, TripStopStep } from './TaskTripDetails.types'

const UNKNOWN_LOCATION_NAME = 'Unknown location'

// `en_route_to` arrives as either a bare id string or a `{ id }` object (see taskProgress.ts) —
// normalise both to a plain id, or null when no leg is in progress.
export const resolveEnRouteToId = (
  enRouteTo: TaskProgress['locations']['enRouteTo'] | undefined,
): string | null => {
  if (!enRouteTo) return null
  return typeof enRouteTo === 'string' ? enRouteTo : enRouteTo.id
}

// A stop is late when it was actually arrived at after its planned arrival time.
// Uses dayjs (not `.getTime()`) because the progress response is not run through its zod schema
// at runtime, so these fields arrive as ISO strings rather than Date instances — dayjs handles
// both, matching how LocationDateCell compares arrival times.
export const isLateArrival = (
  actualArrival: Date | string | null | undefined,
  plannedArrival: Date | string | null | undefined,
): boolean => {
  if (!actualArrival || !plannedArrival) return false
  return dayjs(actualArrival).isAfter(dayjs(plannedArrival))
}

// Builds the ordered list of rows to render: visited stops first (in the order they were visited),
// then the remaining stops, with the vehicle's position marked on exactly one of them.
//
// The backend only populates `en_route_to` while a leg is actually in progress. It returns null
// both while the vehicle is inside a geofence and once the task is completed or cancelled — in
// either case the vehicle is at the stop it visited last, so the marker belongs on that stop
// rather than pointing ahead to the next one.
export const buildTripSteps = (
  progress: TaskProgress,
  locations: TaskTripDetailsLocation[],
): TripStep[] => {
  const locationName = (id: string) =>
    locations.find((location) => location.id === id)?.name ?? UNKNOWN_LOCATION_NAME

  const visitedSteps: TripStopStep[] = progress.locations.visited.map((location) => ({
    kind: 'stop',
    id: location.id,
    name: locationName(location.id),
    plannedArrival: location.plannedArrival,
    actualArrival: location.actualArrival,
    isVehicleHere: false,
  }))

  const remainingSteps: TripStopStep[] = progress.locations.remaining.map((location) => ({
    kind: 'stop',
    id: location.id,
    name: locationName(location.id),
    plannedArrival: location.plannedArrival,
    actualArrival: null,
    isVehicleHere: false,
  }))

  const enRouteToId = resolveEnRouteToId(progress.locations.enRouteTo)

  if (enRouteToId === null) {
    // No leg in progress — the vehicle is standing at the most recently visited stop. With nothing
    // visited yet there is no position to report, so no marker is shown.
    const currentStop = visitedSteps.at(-1)
    if (currentStop) currentStop.isVehicleHere = true

    return [...visitedSteps, ...remainingSteps]
  }

  const enRouteStep: TripStep = { kind: 'enRoute', targetId: enRouteToId }

  // The vehicle is between stops, so the marker gets a row of its own directly in front of the
  // stop being travelled to. The backend only ever targets the first remaining stop; if that ever
  // changes (or the target is unknown) the marker still lands in front of the right row.
  const targetIndex = remainingSteps.findIndex((step) => step.id === enRouteToId)
  const insertionIndex = targetIndex === -1 ? 0 : targetIndex

  return [
    ...visitedSteps,
    ...remainingSteps.slice(0, insertionIndex),
    enRouteStep,
    ...remainingSteps.slice(insertionIndex),
  ]
}
